if (!customElements.get('aplovio-bottom-navigation')) {
  customElements.define('aplovio-bottom-navigation', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const options = {signal: this.abort.signal};
      this.menuTrigger = this.querySelector('[data-bottom-menu]');
      this.searchTrigger = this.querySelector('[data-search-open]');
      this.accountTrigger = this.querySelector('[data-account-open]');
      this.cartTrigger = this.querySelector('[data-cart-open]');
      this.count = this.querySelector('[data-bottom-cart-count]');
      this.accountAltSlot = this.querySelector('[data-ap-account-alt-slot]');
      this.accountLabel = this.accountTrigger?.getAttribute('aria-label') || '';
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.measure();
        });
      };
      this.bind = () => this.bindLayers();
      this.resize = new ResizeObserver(this.schedule);
      this.resize.observe(this);
      window.addEventListener('resize', this.schedule, options);
      document.addEventListener('shopify:section:load', this.bind, options);
      document.addEventListener('aplovio:navigation-ready', this.bind, options);
      document.addEventListener('aplovio:cart-updated', event => this.updateCount(event.detail), options);
      this.accountAltObserver = new MutationObserver(() => this.syncAccountAlternate());
      if (this.accountAltSlot) this.accountAltObserver.observe(this.accountAltSlot, {childList: true, subtree: true});
      if (this.accountTrigger) this.accountAltObserver.observe(this.accountTrigger, {attributes: true, attributeFilter: ['data-ap-account-alt-label']});
      this.accountAltObserver.observe(document.documentElement, {attributes: true, attributeFilter: ['data-ap-account-alt', 'data-fuzelio-rewards']});
      for (const state of ['open', 'close']) {
        document.addEventListener(`aplovio:layer-${state}`, event => {
          for (const trigger of [this.menuTrigger, this.searchTrigger, this.accountTrigger]) {
            const layer = document.getElementById(trigger?.getAttribute('aria-controls'));
            if (layer?.dialog === event.target) trigger.setAttribute('aria-expanded', String(state === 'open'));
          }
        }, options);
      }
      this.addEventListener('click', event => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const trigger = event.target.closest('[data-bottom-menu], [data-search-open]');
        const layer = trigger?.hasAttribute('data-bottom-menu') ? this.menuLayer : this.searchLayer;
        if (!trigger || !this.contains(trigger) || !layer?.open) return;
        event.preventDefault();
        layer.open(trigger);
      }, options);
      this.bindLayers();
      this.syncAccountAlternate();
      customElements.whenDefined('aplovio-layer').then(() => {
        if (this.isConnected && !this.abort?.signal.aborted) this.bindLayers();
      });
    }

    bindLayers() {
      const header = document.querySelector('aplovio-header');
      const headerMenu = header?.querySelector('.ap-header__surface [data-layer-open]');
      this.menuLayer = document.getElementById(headerMenu?.getAttribute('aria-controls'));
      this.searchLayer = header?.querySelector('aplovio-search')?.closest('aplovio-layer');
      const headerAccount = header?.querySelector('.ap-header__surface [data-account-open]');
      const accountLayer = document.getElementById(headerAccount?.getAttribute('aria-controls'));
      for (const [trigger, layer] of [[this.menuTrigger, this.menuLayer], [this.searchTrigger, this.searchLayer], [this.accountTrigger, accountLayer]]) {
        if (!trigger) continue;
        if (layer?.open) {
          trigger.setAttribute('aria-controls', layer.id);
          trigger.setAttribute('aria-expanded', String(Boolean(layer.dialog?.open)));
          trigger.setAttribute('aria-haspopup', 'dialog');
        } else {
          trigger.removeAttribute('aria-controls');
          trigger.removeAttribute('aria-expanded');
          trigger.removeAttribute('aria-haspopup');
        }
      }
      // Ready once Menu and Search can open their layers. Pages marked data-ap-bottom-nav hide the header's own actions
      // from the first paint (aplovio-header.css), and ready ends their 8 s fallback there; elsewhere ready still hides them.
      this.toggleAttribute('data-ready', Boolean(this.menuLayer?.open && this.searchLayer?.open));
      this.schedule();
    }

    updateCount(detail) {
      if (!Number.isSafeInteger(detail?.count) || detail.count < 0 || detail.count > 1_000_000) return;
      this.count.textContent = detail.count > 9 ? '9+' : String(detail.count);
      if (typeof detail.label === 'string' && detail.label.length < 300) this.cartTrigger.setAttribute('aria-label', detail.label);
    }

    syncAccountAlternate() {
      if (!this.accountTrigger || !this.accountAltSlot) return;
      const root = document.documentElement;
      const ready = this.dataset.accountAltEffect === 'true' && root.dataset.apAccountAlt === 'rewards' &&
        root.dataset.fuzelioRewards === 'ready' && Boolean(this.accountAltSlot.querySelector('svg'));
      this.accountAltSlot.hidden = !ready;
      this.accountTrigger.toggleAttribute('data-account-alt-ready', ready);
      const alternateLabel = this.accountTrigger.dataset.apAccountAltLabel.trim();
      if (!ready) this.accountTrigger.setAttribute('aria-label', this.accountLabel);
      else if (alternateLabel) this.accountTrigger.setAttribute('aria-label', alternateLabel);
    }

    measure() {
      const value = `${window.AplovioBars.measure(this).clearance}px`;
      if (this.lastOffset === value) return;
      this.lastOffset = value;
      document.documentElement.style.setProperty('--ap-bottom-nav-height', value);
      window.dispatchEvent(new CustomEvent('aplovio:bottom-navigation-resize'));
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      this.resize?.disconnect();
      this.accountAltObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.lastOffset = null;
      this.removeAttribute('data-ready');
      document.documentElement.style.removeProperty('--ap-bottom-nav-height');
      window.dispatchEvent(new CustomEvent('aplovio:bottom-navigation-resize'));
    }
  });
}
